import useUpgradesStore from "@/store/useUpgradesStore";
import {
	TileEntityParam,
	type TileIndex,
	type TileNeighbour,
} from "@/types/tile";
import { FrameName, tilerepoGrasslandFrameNames } from "../config";
import { ResourceType } from "../config/resources";
import { TileVisibilityType } from "../config/terrain";
import { UpgradeIds } from "../config/upgrades";
import type { TileEntityWithOnBuildMethod } from "../TileEntity";
import TileEntity from "../TileEntity";

class LumberMillBuildingTileEntity
	extends TileEntity
	implements TileEntityWithOnBuildMethod
{
	public static readonly frameName = FrameName.LUMBER_MILL;

	public get requirements(): Map<ResourceType, number> {
		const requirements = new Map<ResourceType, number>(super.requirements);

		// Apply upgrades
		let connectionRequirement =
			(requirements.get(ResourceType.SETTLEMENT_CONNECTION) as number) ||
			0;
		if (
			useUpgradesStore().getCurrentLevelById(UpgradeIds.log_driving) > 0
		) {
			connectionRequirement = 0;
		}
		requirements.set(
			ResourceType.SETTLEMENT_CONNECTION,
			connectionRequirement,
		);

		return requirements;
	}

	public onBuild(): void {
		if (useUpgradesStore().getCurrentLevelById(UpgradeIds.forestry) <= 0)
			return;

		if (!this.range) throw new Error("Range is not defined!");

		const neighbours = this.tilerepo
			.getNeighbourTileIndexesByTileCoordinates(
				{ x: this.x, y: this.y },
				this.range,
			)
			.map((tileIndex: TileIndex): TileNeighbour | null => {
				if (
					this.tilerepo.getParam(
						tileIndex,
						TileEntityParam.IS_BLOCKED_BY_ENEMY,
					)
				)
					return null;

				const [tileId0, , tileId2] =
					this.tilerepo.getTileIdsByTileIndex(tileIndex);
				if (!tileId0 || tileId2) return null;

				const frameName = this.tilerepo.getFrameNameByTileId(tileId0);
				const correctTiles = [
					FrameName.FOREST,
					FrameName.RUINED_FOREST,
					...tilerepoGrasslandFrameNames,
				];
				if (!frameName || !correctTiles.includes(frameName))
					return null;

				return {
					tileIndex,
					tileId: tileId0,
					frameName,
				};
			})
			.filter(
				(neighbour: TileNeighbour | null): boolean =>
					neighbour !== null &&
					this.tilerepo.getParam(
						neighbour.tileIndex,
						TileEntityParam.VISIBILITY,
					) === TileVisibilityType.EXPLORED,
			) as Array<TileNeighbour>;

		for (let i = 0; i < neighbours.length; i++) {
			this.tilerepo.setTileByTileCoordinates(
				this.tilerepo.getTileCoordinatesByTileIndex(
					neighbours[i].tileIndex,
				),
				FrameName.DENSE_FOREST,
				i !== neighbours.length - 1,
			);
		}
	}
}

export default LumberMillBuildingTileEntity;
